import * as React from 'react';

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { listTableStyles } from './listTableStyles';

type DataTableCellVariant = 'default' | 'subtle' | 'empty';

const cellClassNameByVariant: Record<DataTableCellVariant, string> = {
    default: listTableStyles.cell,
    subtle: listTableStyles.subtleCell,
    empty: listTableStyles.emptyCell,
};

const DataTable = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
    ({ className, ...props }, ref) => (
        <Table ref={ref} className={cn('w-full', className)} {...props} />
    ),
);
DataTable.displayName = 'DataTable';

const DataTableHeader = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
    <TableHeader ref={ref} className={cn(listTableStyles.header, className)} {...props} />
));
DataTableHeader.displayName = 'DataTableHeader';

const DataTableBody = TableBody;

const DataTableHeaderRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
    <TableRow ref={ref} className={cn('hover:bg-surface-muted/70', className)} {...props} />
));
DataTableHeaderRow.displayName = 'DataTableHeaderRow';

const DataTableRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
    <TableRow ref={ref} className={cn(listTableStyles.row, className)} {...props} />
));
DataTableRow.displayName = 'DataTableRow';

const DataTableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
    <TableHead ref={ref} className={cn(listTableStyles.head, className)} {...props} />
));
DataTableHead.displayName = 'DataTableHead';

type DataTableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
    variant?: DataTableCellVariant;
};

const DataTableCell = React.forwardRef<HTMLTableCellElement, DataTableCellProps>(
    ({ className, variant = 'default', ...props }, ref) => (
        <TableCell
            ref={ref}
            className={cn(cellClassNameByVariant[variant], className)}
            {...props}
        />
    ),
);
DataTableCell.displayName = 'DataTableCell';

export {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableHeaderRow,
    DataTableRow,
};
